import { useDescope, useUser } from "@descope/react-sdk";
import { Logout, Upload } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  FormHelperText,
  InputLabel,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import Stripe from "stripe";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import Loader from "../../layouts/loader/Loader";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ABAEngineLogo from "../../assets/images/logos/abaengine.png";
import SubscriptionCard from "../../components/payment/SubscriptionCard";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import SubscriptionAddOnCard from "../../components/payment/SubscriptionAddOnCard";
import SubscriptionFinalCard from "../../components/payment/SubscriptionFinalCard";
import { setResponseValue } from "../../redux/features/api-response/api-response";
import { getOrgInfo } from "../../redux/features/organization/organization-slice";
import { RootState, useAppSelector } from "../../redux/store";
import CompanyUserService from "../../service/org.service";
import { isValidFileUploaded } from "../../utils/Image-validation";

import {
  getPlans,
  getAddons,
  getTenantPlan,
} from "../../redux/features/plan-billing/plan-billing-slice";
import { OrgData } from "../../types/company";
import { IStripeProduct, ITenantUsage } from "../../types/product";

const stripe = new Stripe(`${process.env.REACT_APP_STRIPE_SECRET_KEY}`, {
  apiVersion: "2022-11-15",
});

const OrganizationOnboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  //get session token
  const { user } = useUser();
  const sdk = useDescope();

  const org = useSelector<RootState, OrgData>(
    (state) => state.organizationSlice.orgData
  );

  //org name
  const [orgName, setOrgName] = useState<string>(org?.orgName || "");

  //org url
  const [orgUrl, setOrgUrl] = useState<string>(org?.orgUrl || "");

  //org logo file
  const [orgLogoFile, setOrgLogoFile] = useState<any>(null);

  //preview image
  const [previewImage, setPreviewImage] = useState<string>("");

  //handle image error
  const [imageError, setImageError] = useState<string>("");

  const [orgService, setOrgService] = useState<string[]>(org?.orgService || []);

  //for loader component
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paymentPage, setPaymentPage] = useState(false);
  const [addOnPage, setAddOnPage] = useState(false);
  const [planId, setPlanId] = useState(0);
  const [orgExists, setOrgExists] = useState(org?.id !== "" ? true : false);
  const [selectedPlan, setSelectedPlan] = useState<any | null>(null); //main plan product
  
  const products = useSelector<RootState, IStripeProduct[]>(
    (state) => state.planBillingSlice.products
  );
  const mailProducts = useSelector<RootState, any[]>(
    (state) => state.planBillingSlice.addons.mailProducts
  );
  const textProducts = useSelector<RootState, any[]>(
    (state) => state.planBillingSlice.addons.textProducts
  );
  const scheduleProducts = useSelector<RootState, any[]>(
    (state) => state.planBillingSlice.addons.scheduleProducts
  );
  const tenantUsage = useSelector<RootState, ITenantUsage>(
    (state) => state.planBillingSlice.tenantUsage
  );
  const [adminSeats, setAdminSeats] = useState(0);

  const [mailAddCheck, setMailAddCheck] = useState(false);
  const [textAddCheck, setTextAddCheck] = useState(false);
  const [scheduleAddCheck, setScheduleAddCheck] = useState(false);

  const currentPlan = useAppSelector(
    (state) => state.planBillingSlice.currentPlan
  );

  //removes focus when enter is pressed
  const removeFocus = (id: string) => {
    document.getElementById(id)?.blur();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      // dispatch(setProfilePicture(null));
    } else if (isValidFileUploaded(e.target.files[0])) {
      setImageError("");
      const src = URL.createObjectURL(e.target.files[0]);
      setPreviewImage(src);
      setOrgLogoFile(e.target.files[0]);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    let logoFile = orgLogoFile,
      name = orgName;

    try {
      const createOrg = await CompanyUserService.postOrganization(
        name,
        orgUrl,
        logoFile,
        orgService
      );

      if (createOrg) {
        // console.log("createOrg", createOrg);

        //a. make sure customAttribute is re-fetch quickly
        await sdk.selectTenant(createOrg.data.descope_tenantId);
        await sdk.refresh(sdk.getRefreshToken());
        await sdk.me();

        dispatch(setResponseValue({ name: "success", value: true }));
        dispatch(
          setResponseValue({ name: "message", value: createOrg.message })
        );
        dispatch(getOrgInfo({ useLoader: true }));

        //after user & org created
        if (
          user?.email !== undefined &&
          user?.customAttributes?.currentTenantId
        ) {
          setPaymentPage(true);
        }
      }
    } catch (e: any) {
      console.log("error", e);
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(setResponseValue({ name: "message", value: e?.message }));
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
      setIsLoading(false);
    }
  };

  const steps = [
    "Register User",
    "Setup Organization",
    //"Invite Team Members",
    "Choose Plan",
    "Choose Add-On",
    "Onboarding Complete",
  ];

  const handleUserLogout = async () => {
    localStorage.removeItem("selectedTab");
    localStorage.removeItem("setCounter");
    await sdk.logout();
    navigate("/");
  };

  useEffect(() => {
    setOrgExists(org?.id !== "" ? true : false);

    //check if user has a tenantId --> if yes, setup org complete.
    //then show payment page
    if (user?.customAttributes?.currentTenantId) {
      //check if org.enterpriseApprove is true, if yes, bypass payment page
      //CTO Review: ideally, we also want to check on planId, subscriptionId before allowing to dashboard.
      if (org && org.enterpriseApprove) {
        //success, redirect to my-plan
        navigate("/my-plan");
      } else {
        setPaymentPage(true);
      }
    }
  }, [user, org]);

  useEffect(() => {
    console.log("paymentPage", products.length, paymentPage)
    if (!products || products.length === 0) {
      if (paymentPage) {
        // Properly handle the async dispatches
        Promise.all([
          dispatch(getPlans()),
          dispatch(getAddons())
        ]).catch(error => {
          console.error('Error loading plans and addons:', error);
          dispatch(setResponseValue({ 
            name: "error", 
            value: true 
          }));
          dispatch(setResponseValue({ 
            name: "message", 
            value: "Failed to load plans and addons" 
          }));
        });
      }
    }

    if (org?.id !== "" && user?.customAttributes?.currentTenantId) {
      dispatch(getTenantPlan());
    }
  }, [
    paymentPage,
    currentPlan?.currentPlanId,
    org?.id,
    org?.currentPlanId,
    org?.adminSeats,
    user?.customAttributes?.currentTenantId,
    dispatch,  // Add dispatch to dependencies
    products   // Add products to dependencies
  ]);

  useEffect(() => {
    if (addOnPage) setPaymentPage(false);
  }, [addOnPage]);

  useEffect(() => {
    if (planId !== undefined) {
      setSelectedPlan({
        ...currentPlan,
        planDetail: products[planId]
      });
    }
  }, [planId, products, currentPlan]);

  console.log("currentPlan", currentPlan);
  console.log("selectedPlan", selectedPlan);

  // console.log("adminSeats: ", adminSeats);
  // console.log("mailProducts: ", mailProducts);
  // console.log("textProducts: ", textProducts);
  // console.log("scheduleProducts: ", scheduleProducts);
  // console.log("orgExists:", orgExists, user, org);

  return (
    <Stack direction={"row"} sx={{ height: "100vh" }}>
      <Box
        sx={{
          backgroundColor: "#F6FBFF",
          width: "20%",
          paddingX: "32px",
          paddingTop: "62px",
        }}
      >
        <Box
          component={"img"}
          src={ABAEngineLogo}
          sx={{ width: "211px", marginBottom: "34px" }}
        ></Box>
        <Box>
          <Stepper
            orientation="vertical"
            activeStep={paymentPage ? 2 : addOnPage ? 3 : 1}
          >
            {steps.map((step) => (
              <Step key={step}>
                <StepLabel>{step}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Box>
      <Box
        sx={{
          paddingX: "116px",
          paddingTop: "50px",
          width: "100%",
        }}
      >
        {isLoading && <Loader isShow={isLoading} />}

        {/* Setup Organization */}
        {!orgExists && user && (
          <ValidatorForm onSubmit={handleSubmit}>
            <Typography
              sx={{
                fontSize: "36px",
                fontWeight: "700",
                marginBottom: "70px",
              }}
            >
              Setup Organization
            </Typography>
            <Box sx={{ marginBottom: "20px" }}>
              <InputLabel
                sx={{
                  fontSize: "18px",
                  fontWeight: "500",
                  marginBottom: "10px",
                }}
              >
                Company Name *
              </InputLabel>
              <TextValidator
                id="orgName"
                type="text"
                name="orgName"
                value={orgName}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    removeFocus("orgName");
                  }
                }}
                style={{ width: "100%" }}
                onChange={(e: any) => setOrgName(e.target.value)}
                validators={["required"]}
                errorMessages={["Organization name is required"]}
              />
            </Box>
            <Box className="fromGroup">
              <InputLabel
                sx={{
                  fontSize: "18px",
                  fontWeight: "500",
                  marginBottom: "10px",
                }}
                // className="fromLabel"
              >
                Company Service *
              </InputLabel>
              <div className="fromGroup-chips">
                {["Autism Practice", "Mental Health"].map((item) => (
                  <Card
                    className={
                      orgService.includes(item) ? "fromGroup-chip-active" : ""
                    }
                    onClick={() => {
                      if (orgService.includes(item)) {
                        return setOrgService((pre) =>
                          pre.filter((i) => i !== item)
                        );
                      }
                      setOrgService((pre) => [item, ...pre]);
                    }}
                    key={item}
                  >
                    {item}
                  </Card>
                ))}
                <div style={{ width: "100%" }} />
                {false && (
                  <FormHelperText style={{ color: "#d32f2f" }}>
                    Please select at least 1 service
                  </FormHelperText>
                )}
              </div>
            </Box>
            <Box sx={{ marginBottom: "40px" }}>
              <InputLabel
                sx={{
                  fontSize: "18px",
                  fontWeight: "500",
                  marginBottom: "10px",
                }}
              >
                Company Website *
              </InputLabel>
              <TextValidator
                id="orgUrl"
                type="text"
                name="orgUrl"
                value={orgUrl}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    removeFocus("orgUrl");
                  }
                }}
                style={{ width: "100%" }}
                onChange={(e: any) => setOrgUrl(e.target.value)}
                validators={["required"]}
                errorMessages={["Company website is required"]}
              />
            </Box>
            <Box sx={{ marginBottom: "40px" }}>
              {previewImage && (
                <Box
                  component={"img"}
                  src={previewImage}
                  alt="Company Logo"
                  sx={{
                    width: "100%",
                    height: "206px",
                    objectFit: "contain",
                  }}
                ></Box>
              )}
              <Button
                variant="contained"
                component="label"
                className="uploadCompanyProfile__btn"
                startIcon={<Upload />}
              >
                Upload Company Logo
                <input
                  accept="image/png, image/jpeg"
                  type="file"
                  hidden
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleFileChange(e);
                  }}
                />
              </Button>
              <FormHelperText
                style={{
                  color: "#d32f2f",
                  position: "absolute",

                  left: "60px",
                }}
              >
                {imageError}
              </FormHelperText>
            </Box>
            <Button
              type="submit"
              sx={{
                paddingX: "24px",
                paddingY: "8px",
                fontSize: "18px",
                fontWeight: "500",
                marginLeft: "auto",
                display: "flex",
              }}
            >
              Next
            </Button>
          </ValidatorForm>
        )}

        {/* Choose Plan */}
        {paymentPage && (
          <>
            <Grid
              display="flex"
              justifyContent="space-between"
              sx={{ m: "0px 0px 10px 0px" }}
            >
              <Box>
                <Typography variant="h5" fontWeight={"550"}>
                  Hi, {user.name}! What's Plan You'd like to choose?
                </Typography>
                {/* <Typography fontSize={"18px"} marginTop={"10px"}>
                  If you are not sure, you can book a demo with us
                </Typography>
                <Button
                  sx={{
                    backgroundColor: "#096dd9",
                    color: "white",
                    padding: "10px 30px",
                    fontSize: "15px",
                    marginTop: "25px",
                  }}
                >
                  Book Demo
                </Button> */}
              </Box>

              <Logout
                onClick={handleUserLogout}
                className="logout-icon"
                sx={{ marginBottom: "5px" }}
              />
            </Grid>
            <Grid container spacing={3}>
              {products.map((product: IStripeProduct, index: number) => (
                <SubscriptionCard
                  key={index}
                  product={product}
                  tenantUsage={tenantUsage}
                  adminSeats={adminSeats}
                  setAdminSeats={setAdminSeats}
                  setAddOnPage={setAddOnPage}
                  setPlanId={setPlanId}
                />
              ))}
            </Grid>
          </>
        )}

        {addOnPage && (
          <>
            <Grid
              display="flex"
              justifyContent="space-between"
              sx={{ m: "0px 0px 10px 0px" }}
            >
              <Box>
                <Typography variant="h5" fontWeight={"550"}>
                  Hi, {user.name}! Would you like to add some other features?
                </Typography>
                <Typography fontSize={"18px"} marginTop={"10px"}>
                  Here are the add-ons you can add to your subscription. 
                </Typography>
              </Box>

              <Logout
                onClick={handleUserLogout}
                className="logout-icon"
                sx={{ marginBottom: "5px" }}
              />
            </Grid>
            <Grid container spacing={3}>
              <SubscriptionAddOnCard
                product={mailProducts[planId]}
                type={0}
                setAddCheck={setMailAddCheck}
                addCheck={mailAddCheck}
              />
              <SubscriptionAddOnCard
                product={textProducts[planId]}
                type={1}
                setAddCheck={setTextAddCheck}
                addCheck={textAddCheck}
              />
              <SubscriptionAddOnCard
                product={scheduleProducts[planId]}
                type={2}
                setAddCheck={setScheduleAddCheck}
                addCheck={scheduleAddCheck}
              />
            </Grid>
            <Grid container spacing={3}>
              <SubscriptionFinalCard
                product={products[planId]}
                mailProduct={mailProducts[planId]}
                textProducts={textProducts}
                scheduleProduct={scheduleProducts[planId]}
                mailAddCheck={mailAddCheck}
                textAddCheck={textAddCheck}
                scheduleAddCheck={scheduleAddCheck}
                addOnPage={addOnPage}
                adminSeats={adminSeats}
                selectedPlan={selectedPlan}
                smsPriceId={""}
                mailboxSeats={0}
                showCouponField={false} // no need to show coupon field here, since it will be on Stripe checkout. 
                isUpdate={false} // create plan
              />
            </Grid>
          </>
        )}
      </Box>
    </Stack>
  );
};

export default OrganizationOnboard;
